/* ==========================================================================
   Deco Deco theme — assets/theme.js
   Global behaviour. Vanilla ES2020, no dependencies, loaded with `defer`.
   Modules: money formatting, toast, drawers, dialogs, popouts (custom selects),
   cart API + line interactions, carousels, reveal animations, header/scroll-top,
   cookie consent (Shopify Customer Privacy API), contact form, login recover.
   ========================================================================== */
(() => {
  'use strict';

  const T = (window.theme = window.theme || {});
  T.routes = T.routes || {};
  T.strings = T.strings || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------------------------------------------------------------- money */
  const formatMoney = (cents, format = T.moneyFormat || '${{amount}}') => {
    const value = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    const type = match ? match[1] : 'amount';
    const fmt = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const [int, dec] = fixed.split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? grouped + decimal + dec : grouped;
    };
    let out;
    switch (type) {
      case 'amount_no_decimals': out = fmt(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = fmt(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = fmt(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = fmt(value, 2, "'", '.'); break;
      case 'amount_with_space_separator': out = fmt(value, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': out = fmt(value, 0, ' ', ','); break;
      default: out = fmt(value, 2, ',', '.');
    }
    return format.replace(placeholder, out);
  };
  T.formatMoney = formatMoney;

  /* ---------------------------------------------------------------- toast */
  let toastTimer;
  T.toast = (message) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3500);
  };

  /* ---------------------------------------------------------- scroll lock */
  let lockCount = 0;
  const lockScroll = () => { lockCount += 1; document.body.classList.add('scroll-locked'); };
  const unlockScroll = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.body.classList.remove('scroll-locked'); };

  /* -------------------------------------------------------------- drawers */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let activeDrawer = null;
  let lastFocus = null;

  const openDrawer = (id) => {
    const drawer = document.getElementById(id);
    if (!drawer || !drawer.hidden) return;
    if (activeDrawer) closeDrawer(activeDrawer, true);
    lastFocus = document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('is-open')));
    lockScroll();
    activeDrawer = drawer;
    $$(`[data-drawer-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const first = $('.drawer__head [data-drawer-close]', drawer) || $(FOCUSABLE, drawer);
    if (first) setTimeout(() => first.focus(), 80);
  };

  const closeDrawer = (drawer, immediate = false) => {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    const finish = () => { drawer.hidden = true; };
    if (immediate) finish(); else setTimeout(finish, 360);
    unlockScroll();
    $$(`[data-drawer-open="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (activeDrawer === drawer) activeDrawer = null;
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  };

  T.openDrawer = openDrawer;
  T.closeDrawer = (id) => closeDrawer(document.getElementById(id));

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(opener.dataset.drawerOpen); return; }

    const cartOpen = e.target.closest('[data-cart-open]');
    if (cartOpen && document.getElementById('CartDrawer') && !(e.metaKey || e.ctrlKey || e.shiftKey)) {
      e.preventDefault();
      openDrawer('CartDrawer');
      return;
    }

    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { e.preventDefault(); closeDrawer(closer.closest('[data-drawer]')); }
  });

  document.addEventListener('keydown', (e) => {
    if (!activeDrawer) return;
    if (e.key === 'Escape') { closeDrawer(activeDrawer); return; }
    if (e.key === 'Tab') {
      const items = $$(FOCUSABLE, activeDrawer).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* -------------------------------------------------------------- dialogs */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog && typeof dialog.showModal === 'function') { e.preventDefault(); dialog.showModal(); }
      return;
    }
    const closer = e.target.closest('[data-dialog-close], [data-charm-modal-close], [data-lightbox-close]');
    if (closer) { const dialog = closer.closest('dialog'); if (dialog) dialog.close(); return; }
    if (e.target instanceof HTMLDialogElement && e.target.open) e.target.close(); // click on the backdrop area
  });

  /* Charm suite preview */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-charm-preview]');
    if (!btn) return;
    const modal = $('[data-charm-modal]');
    if (!modal) return;
    const img = $('[data-charm-modal-img]', modal);
    img.src = btn.dataset.image;
    img.alt = btn.dataset.title || '';
    $('[data-charm-modal-title]', modal).textContent = btn.dataset.title || '';
    const note = $('.charm-modal__note', modal);
    if (note) note.hidden = btn.dataset.note === 'hide'; // the substitution note only applies to charm suites
    modal.showModal();
  });

  /* ------------------------------------------------------------- popouts */
  const setPopout = (popout, open) => {
    const toggle = $('[data-popout-toggle]', popout);
    const list = $('[data-popout-list]', popout);
    if (!toggle || !list) return;
    popout.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    list.hidden = !open;
    if (open) {
      const current = $('.popout__option.is-current', list) || $('.popout__option', list);
      if (current) current.focus();
    }
  };
  const closeAllPopouts = (except) => $$('[data-popout].is-open').forEach((p) => { if (p !== except) setPopout(p, false); });

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-popout-toggle]');
    if (toggle) {
      const popout = toggle.closest('[data-popout]');
      const open = !popout.classList.contains('is-open');
      closeAllPopouts(popout);
      setPopout(popout, open);
      return;
    }
    const option = e.target.closest('[data-popout-option]');
    if (option) {
      const popout = option.closest('[data-popout]');
      const input = $('[data-popout-input]', popout);
      const label = $('[data-popout-label]', popout);
      $$('[data-popout-option]', popout).forEach((o) => {
        const current = o === option;
        o.classList.toggle('is-current', current);
        o.setAttribute('aria-selected', String(current));
      });
      if (label) label.textContent = option.textContent.trim();
      if (input) { input.value = option.dataset.value; input.dispatchEvent(new Event('change', { bubbles: true })); }
      setPopout(popout, false);
      $('[data-popout-toggle]', popout).focus();
      if (popout.hasAttribute('data-submit-on-change')) {
        const form = popout.closest('form');
        if (form) (typeof form.requestSubmit === 'function' ? form.requestSubmit() : form.submit());
      }
      return;
    }
    if (!e.target.closest('[data-popout]')) closeAllPopouts();
  });

  document.addEventListener('keydown', (e) => {
    const popout = e.target.closest('[data-popout]');
    if (!popout) return;
    if (e.key === 'Escape') { setPopout(popout, false); $('[data-popout-toggle]', popout).focus(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const options = $$('[data-popout-option]', popout);
      if (!options.length) return;
      e.preventDefault();
      if (!popout.classList.contains('is-open')) { setPopout(popout, true); return; }
      const i = options.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % options.length : (i - 1 + options.length) % options.length;
      options[next].focus();
    }
  });

  /* ------------------------------------------------------------ cart API */
  const SECTIONS = 'cart-drawer,cart-items';
  const pick = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);

  /* Only the header badge is a display target; the drawer panel and cart page carry data-cart-count as data. */
  const updateCount = (count) => {
    $$('[data-cart-badge]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
    T.cartCount = count;
  };

  const renderCart = (sections) => {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const fresh = pick(sections['cart-drawer'], '[data-cart-drawer-content]');
      const current = $('[data-cart-drawer-content]');
      if (fresh && current) current.replaceWith(fresh);
      if (fresh) updateCount(Number(fresh.dataset.cartCount) || 0);
    }
    if (sections['cart-items']) {
      const fresh = pick(sections['cart-items'], '[data-cart-page]');
      const current = $('[data-cart-page]');
      if (fresh && current) current.replaceWith(fresh);
    }
    document.dispatchEvent(new CustomEvent('cart:updated'));
  };

  const cartRequest = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || T.strings.genericError || 'Something went wrong.');
    return data;
  };

  const withSections = (body) => ({ ...body, sections: SECTIONS, sections_url: window.location.pathname });

  T.cart = {
    async add(items) {
      const data = await cartRequest(`${T.routes.cartAdd || '/cart/add'}.js`, withSections({ items }));
      renderCart(data.sections);
      return data;
    },
    async change(key, quantity) {
      const data = await cartRequest(`${T.routes.cartChange || '/cart/change'}.js`, withSections({ id: key, quantity }));
      renderCart(data.sections);
      return data;
    },
    async update(updates, extra = {}) {
      const data = await cartRequest(`${T.routes.cartUpdate || '/cart/update'}.js`, withSections({ updates, ...extra }));
      renderCart(data.sections);
      return data;
    },
  };

  const showCartError = (message) => {
    const box = $('[data-cart-errors]');
    if (box) { box.textContent = message; box.hidden = false; } else T.toast(message);
  };
  const setLineLoading = (key, on) => $$('[data-cart-line]').forEach((line) => {
    if (line.dataset.lineKey === key) line.classList.toggle('is-loading', on);
  });

  /* Quantity steppers (product form + cart lines) */
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-qty-minus]');
    const plus = e.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;
    const wrap = (minus || plus).closest('[data-qty]');
    const input = $('[data-qty-input]', wrap);
    if (!input) return;
    const min = Number(input.min || 0);
    const max = input.max ? Number(input.max) : Infinity;
    let value = Number(input.value) || 0;
    if (plus && value + 1 > max) { T.toast((T.strings.onlyLeft || 'Only __COUNT__ left').replace('__COUNT__', max)); return; }
    value = plus ? value + 1 : Math.max(min, value - 1);
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  /* Typed quantities are clamped to the stock limit carried by max="" */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (!input || !input.max) return;
    const max = Number(input.max);
    if (Number(input.value) > max) {
      input.value = max;
      T.toast((T.strings.onlyLeft || 'Only __COUNT__ left').replace('__COUNT__', max));
    }
  }, true);

  /* Cart line quantity + removal. A set (bundle) scales its parent and every component line together,
     so quantity 2 is two identical sets with the same picks. */
  const bundleUpdates = (input, qty) => {
    const line = input.closest('[data-cart-line]');
    const current = Math.max(1, Number(input.dataset.bundleQty) || 1);
    const updates = { [input.dataset.lineKey]: qty };
    $$('[data-component-key]', line).forEach((row) => {
      const perSet = Math.max(1, Math.round((Number(row.dataset.componentQty) || 1) / current));
      updates[row.dataset.componentKey] = perSet * qty;
    });
    return updates;
  };

  let qtyTimer;
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input][data-line-key]');
    if (!input) return;
    clearTimeout(qtyTimer);
    qtyTimer = setTimeout(async () => {
      const key = input.dataset.lineKey;
      const qty = Math.max(0, Number(input.value) || 0);
      setLineLoading(key, true);
      try {
        if (input.hasAttribute('data-bundle-qty')) await T.cart.update(bundleUpdates(input, qty));
        else await T.cart.change(key, qty);
      } catch (err) {
        showCartError(err.message);
        setLineLoading(key, false);
      }
    }, 250);
  });

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-line-remove]');
    if (!remove) return;
    e.preventDefault();
    const line = remove.closest('[data-cart-line]');
    const key = remove.dataset.lineKey;
    setLineLoading(key, true);
    try {
      if (remove.dataset.bundleRemove) {
        const keys = [key, ...$$('[data-component-key]', line).map((el) => el.dataset.componentKey)];
        const updates = {};
        keys.forEach((k) => { updates[k] = 0; });
        await T.cart.update(updates);
      } else {
        await T.cart.change(key, 0);
      }
    } catch (err) {
      showCartError(err.message);
      setLineLoading(key, false);
    }
  });

  /* --------------------------------------------------------- card forms */
  /* Single-variant product cards: quantity + "Add to cart" (opens the drawer) or "Buy now!" (goes to the
     cart page). Without JS the same form posts to /cart/add and Shopify shows the cart page. */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-card-form]');
    if (!form) return;
    e.preventDefault();
    const button = e.submitter || $('[data-card-buy-now]', form);
    const buyNow = button && button.hasAttribute('data-card-buy-now');
    const id = Number(($('[name="id"]', form) || {}).value);
    const qtyInput = $('[name="quantity"]', form);
    const quantity = Math.max(1, Number(qtyInput && qtyInput.value) || 1);
    if (!id) return;
    const original = button ? button.textContent : '';
    if (button) { button.disabled = true; button.textContent = T.strings.adding || 'Adding…'; }
    try {
      await T.cart.add([{ id, quantity }]);
      if (buyNow) { window.location.href = T.routes.cart || '/cart'; return; }
      if (button) { button.disabled = false; button.textContent = original; }
      if (qtyInput) qtyInput.value = 1;
      openDrawer('CartDrawer');
    } catch (err) {
      if (button) { button.disabled = false; button.textContent = original; }
      T.toast(err.message || T.strings.genericError);
    }
  });

  /* ------------------------------------------------------------ carousel */
  $$('[data-carousel]').forEach((carousel) => {
    const track = $('[data-carousel-track]', carousel);
    const prev = $('[data-carousel-prev]', carousel);
    const next = $('[data-carousel-next]', carousel);
    if (!track) return;
    const step = () => {
      const item = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
    };
    /* Center the arrows on the card image, not on the whole card (image + title + price + button). */
    const alignArrows = () => {
      const media = $('.card__media, .media', track);
      if (!media) return;
      // -8px: the card's hard drop shadow sits below the image, so the visual middle is a touch above the box middle
      const top = media.getBoundingClientRect().top - carousel.getBoundingClientRect().top + media.offsetHeight / 2 - 8;
      carousel.style.setProperty('--arrow-top', `${Math.round(top)}px`);
    };
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step() * 2, behavior: 'smooth' }));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', () => { update(); alignArrows(); });
    update();
    alignArrows();
    window.addEventListener('load', alignArrows);
  });

  /* -------------------------------------------------------------- reveal */
  const reveal = () => {
    const items = $$('[data-reveal]:not(.is-visible)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) { items.forEach((i) => i.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((i) => io.observe(i));
  };
  T.reveal = reveal;
  reveal();

  /* --------------------------------------------------- header / scroll top */
  const header = $('[data-header]');
  const scrollTop = $('[data-scroll-top]');
  const onScroll = () => {
    const y = window.scrollY;
    if (header) header.classList.toggle('is-scrolled', y > 8);
    if (scrollTop) {
      const show = y > 600;
      scrollTop.hidden = !show;
      scrollTop.classList.toggle('is-visible', show);
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  if (scrollTop) scrollTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));

  /* Desktop menu fits or it does not: when the bar would overflow (long menu titles, zoomed browser,
     narrow desktop window), fall back to the burger instead of wrapping the links. Measured with the
     compact class removed, inside one frame, so nothing flickers. */
  const bar = header ? $('.site-header__bar', header) : null;
  const fitHeader = () => {
    if (!bar) return;
    bar.classList.remove('is-compact');
    if (bar.scrollWidth > bar.clientWidth + 1) bar.classList.add('is-compact');
  };
  let fitTimer;
  window.addEventListener('resize', () => { clearTimeout(fitTimer); fitTimer = setTimeout(fitHeader, 80); });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitHeader);
  fitHeader();

  /* -------------------------------------------------------- contact form */
  const contactForm = $('[data-contact-form]');
  if (contactForm) {
    const reason = $('[data-contact-reason] [data-popout-input]', contactForm);
    const guests = $('[data-contact-guests]', contactForm);
    if (reason && guests) reason.addEventListener('change', () => { guests.hidden = reason.value !== 'Event Inquiry'; });
    contactForm.addEventListener('submit', (e) => {
      if (reason && !reason.value) {
        e.preventDefault();
        T.toast('Please choose a reason for contact.');
        const toggle = $('[data-contact-reason] [data-popout-toggle]', contactForm);
        if (toggle) toggle.focus();
      }
    });
  }

  /* ------------------------------------------------------- login recover */
  const loginPanel = $('[data-login-panel]');
  const recoverPanel = $('[data-recover-panel]');
  if (loginPanel && recoverPanel) {
    const showRecover = (on) => { loginPanel.hidden = on; recoverPanel.hidden = !on; };
    $$('[data-recover-open]').forEach((el) => el.addEventListener('click', (e) => { e.preventDefault(); showRecover(true); }));
    $$('[data-recover-close]').forEach((el) => el.addEventListener('click', (e) => { e.preventDefault(); showRecover(false); }));
    if (window.location.hash === '#recover' || $('.form__success', recoverPanel)) showRecover(true);
  }
})();
